import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../eco-icon/EcoIcon";
import { listSupervisorStoresApi } from "../../../views/supervisor/supervisorApi";

import "./EvalSheet.css";

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const shortBr = (ymd: string) => {
  const [, m, d] = ymd.split("-");
  return `${d}/${m}`;
};

export type StoreForEvaluation = {
  id: string;
  nomeFantasia: string;
  endereco?: string | null;
  estado?: string | null;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  token: string;
  buildEvaluationPath: (args: { storeId: string; dayYmd: string }) => string;
  onSelected?: (args: { storeId: string; dayYmd: string }) => void;
};

const STORES_LIMIT = 8;

/**
 * "Avaliar loja" (redesign de 15/10/2026): loja e dia numa etapa só. A busca
 * filtra pelo nome no backend; "Mostrar mais" acrescenta a próxima página.
 */
const SelectStoresForEvaluationModal: React.FC<Props> = ({
  isOpen,
  onClose,
  token,
  buildEvaluationPath,
  onSelected,
}) => {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [stores, setStores] = useState<StoreForEvaluation[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [selectedStore, setSelectedStore] = useState<StoreForEvaluation | null>(null);
  const [dayYmd, setDayYmd] = useState<string>(toYmd(new Date()));

  const today = toYmd(new Date());
  const yesterday = toYmd(new Date(Date.now() - 24 * 60 * 60 * 1000));

  const fetchStores = useCallback(
    async (targetPage: number, nome: string) => {
      if (!token) return;
      try {
        setLoading(true);
        const data = await listSupervisorStoresApi({
          token,
          page: targetPage,
          limit: STORES_LIMIT,
          nome: nome || undefined,
        });
        const items = data.items ?? [];
        setStores((prev) => (targetPage === 1 ? items : [...prev, ...items]));
        setTotal(data.total ?? 0);
      } catch (err: any) {
        toast.error(err?.message ?? "Erro ao buscar lojas.");
      } finally {
        setLoading(false);
      }
    },
    [token],
  );

  // Abriu: estado limpo.
  useEffect(() => {
    if (!isOpen) return;
    if (!token) {
      toast.error("Sessão inválida. Faça login novamente.");
      onClose();
      return;
    }
    setSearch("");
    setQuery("");
    setPage(1);
    setStores([]);
    setTotal(0);
    setSelectedStore(null);
    setDayYmd(toYmd(new Date()));
  }, [isOpen, token, onClose]);

  // Busca com pausa de digitação.
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    fetchStores(page, query);
  }, [isOpen, page, query, fetchStores]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  const handleConfirm = () => {
    if (!selectedStore) {
      toast.error("Selecione uma loja.");
      return;
    }
    if (!dayYmd) {
      toast.error("Selecione uma data.");
      return;
    }
    const args = { storeId: selectedStore.id, dayYmd };
    onSelected?.(args);
    navigate(buildEvaluationPath(args), { state: { selectedStore, referenceDate: dayYmd } });
    onClose();
  };

  if (!isOpen) return null;

  const remaining = Math.max(0, total - stores.length);

  return (
    <div className="evalSheet__overlay" onClick={onClose} role="presentation">
      <div
        className="evalSheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="evalStore-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="evalSheet__grabber" aria-hidden="true">
          <span />
        </div>
        <header className="evalSheet__header">
          <div className="evalSheet__titles">
            <h2 id="evalStore-title" className="evalSheet__title">
              Avaliar loja
            </h2>
            <span className="evalSheet__subtitle">Escolha a loja e o dia da auditoria.</span>
          </div>
          <button type="button" className="evalSheet__close" onClick={onClose} aria-label="Fechar" data-modal-close>
            <EcoIcon name="x" size={20} />
          </button>
        </header>

        <div className="evalSheet__body">
          <div className="evalSheet__search">
            <EcoIcon name="search" size={18} />
            <input
              type="search"
              aria-label="Buscar loja"
              placeholder="Buscar loja"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <fieldset className="evalSheet__group">
            <legend className="evalSheet__label">Loja</legend>
            {loading && stores.length === 0 ? (
              <div className="evalSheet__empty">Carregando lojas...</div>
            ) : stores.length === 0 ? (
              <div className="evalSheet__empty">Nenhuma loja encontrada.</div>
            ) : (
              stores.map((store) => {
                const selected = selectedStore?.id === store.id;
                return (
                  <label
                    key={store.id}
                    className={`evalSheet__option${selected ? " evalSheet__option--selected" : ""}`}
                  >
                    <input
                      type="radio"
                      name="evalStore"
                      checked={selected}
                      onChange={() => setSelectedStore(store)}
                    />
                    <span className="evalSheet__optionText">
                      <span className="evalSheet__optionTitle">{store.nomeFantasia}</span>
                      <span className="evalSheet__optionSub">
                        {store.endereco || "Endereço não informado"}
                        {store.estado ? ` · ${store.estado}` : ""}
                      </span>
                    </span>
                  </label>
                );
              })
            )}
            {remaining > 0 ? (
              <button
                type="button"
                className="evalSheet__more"
                onClick={() => setPage((p) => p + 1)}
                disabled={loading}
              >
                {loading ? "Carregando..." : `Mostrar mais ${remaining} ${remaining === 1 ? "loja" : "lojas"}`}
              </button>
            ) : null}
          </fieldset>

          <div className="evalSheet__group">
            <span className="evalSheet__label">Dia da auditoria</span>
            <div className="evalSheet__dateRow">
              <button
                type="button"
                className="evalSheet__chip"
                aria-pressed={dayYmd === today}
                onClick={() => setDayYmd(today)}
              >
                Hoje, {shortBr(today)}
              </button>
              <button
                type="button"
                className="evalSheet__chip"
                aria-pressed={dayYmd === yesterday}
                onClick={() => setDayYmd(yesterday)}
              >
                Ontem
              </button>
              <input
                type="date"
                className="evalSheet__date"
                aria-label="Outra data"
                value={dayYmd}
                max={today}
                onChange={(e) => setDayYmd(e.target.value)}
              />
            </div>
          </div>
        </div>

        <footer className="evalSheet__footer">
          <button
            type="button"
            className="ecoBtn ecoBtn--primary"
            onClick={handleConfirm}
            disabled={!selectedStore || !dayYmd}
          >
            {selectedStore ? `Continuar com ${selectedStore.nomeFantasia}` : "Escolha a loja"}
            <EcoIcon name="arrowRight" size={18} />
          </button>
        </footer>
      </div>
    </div>
  );
};

export default SelectStoresForEvaluationModal;
